import { useEffect, useEffectEvent } from 'react';

import type { Unsubscribe } from '@mint-lib/api-zod-support/socket';
import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useQueryClient } from '@mint-lib/query-client';
import { useFlags } from '@mint-lib/routing-context';
import { MediaPlan } from '@mint-sdk/api-media-plan';
import {
  onActivityCreated,
  onActivityDeleted,
  onActivitySummaryUpdated,
  onActivityUpdated,
  onFlightCreated,
  onFlightDeleted,
  onFlightSummaryUpdated,
  onFlightUpdated,
  onJoinFailed,
  onMediaLineCreated,
  onMediaLineDeleted,
  onMediaLineUpdated,
  onMediaPlanSummaryUpdated,
  onMediaPlanTotalsUpdated,
  onPlanningColumnsUpdated,
  type mediaLineUpdatedPayloadSchema,
} from '@mint-sdk/api-media-plan/socket';
import { getRouteApi } from '@mint/routing';
import { z } from 'zod';

import { useActivitySummaryApi } from '../../api/queries/useActivitySummaryApi.js';
import { useColumnsApi } from '../../api/queries/useColumnsApi.js';
import { useFlightSummaryApi } from '../../api/queries/useFlightSummaryApi.js';
import { useMediaPlanSummaryApi } from '../../api/queries/useMediaPlanSummaryApi.js';
import { usePreviewActivityListApi } from '../../api/queries/usePreviewActivityListApi.js';
import { useSidebarTreeApi } from '../../api/queries/useSidebarTreeApi.js';
import { usePlanningGridApi } from '../../atoms/gridApi.js';
import { MP_LEVELS } from '../../consts/index.js';
import { chooseLevel } from '../../utils/chooseLevel.js';
import { getRouteByRowId } from '../../utils/grid.utils.js';
import { useChosenLevel } from '../useChosenLevel.js';
import { useColumnsView } from '../useColumnsView.js';

const routerApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan',
);

const isIncomingUpdateOutdated = (
  currentUpdatedAt?: string,
  incomingUpdatedAt?: string,
): boolean => {
  if (!currentUpdatedAt || !incomingUpdatedAt) return false;
  return new Date(incomingUpdatedAt) < new Date(currentUpdatedAt);
};

export const useSocketEvents = () => {
  const { t } = useTranslation('@myn/planning_v2');
  const ff = useFlags('@mint/arm', ['relatedFieldsFixPlaceholder']);
  const navigate = routerApi.useNavigate();
  const { id } = useChosenLevel();
  const { campaignId: planId } = routerApi.useParams();
  const { mediaPlan: planMediaDataId } = routerApi.useSearch();
  const { refetch: refetchSidebarTree } = useSidebarTreeApi();
  const { refetch: refetchPreviewActivityList } = usePreviewActivityListApi();
  const queryClient = useQueryClient();
  const [gridApi] = usePlanningGridApi();
  const { refetch: refetchMediaPlanSummary } = useMediaPlanSummaryApi({
    enabled: false,
  });
  const { refetch: refetchActivitySummary } = useActivitySummaryApi({
    enabled: false,
  });
  const { refetch: refetchFlightSummary } = useFlightSummaryApi({
    enabled: false,
  });
  const { refetch: refetchColumns } = useColumnsApi({ enabled: false });
  const { viewType: columnsViewType } = useColumnsView();

  const handleJoinFailed = useEffectEvent(() => {
    enqueueSnackbar({
      title: t('Failed to connect to background updates'),
      subTitle: t('Background updates will not work'),
      variant: 'error',
      removable: true,
    });
  });

  const handleActivityCreated = useEffectEvent(() => {
    refetchSidebarTree();
    refetchPreviewActivityList();
  });

  const handleActivityDeleted = useEffectEvent(() => {
    refetchSidebarTree();
    refetchPreviewActivityList();
  });

  const handleActivityUpdated = useEffectEvent(() => {
    refetchSidebarTree();
    refetchPreviewActivityList();
  });

  const handleFlightCreated = useEffectEvent(() => {
    refetchSidebarTree();
  });

  const handleFlightUpdated = useEffectEvent(
    (message: z.infer<typeof onFlightUpdated.config.payloadSchema>) => {
      if (message.isNameUpdated) refetchSidebarTree();
    },
  );

  const handleFlightDeleted = useEffectEvent(() => {
    refetchSidebarTree();
  });

  const handleMediaLineDeleted = useEffectEvent(
    ({ data }: z.infer<typeof onMediaLineDeleted.config.payloadSchema>) => {
      const route = getRouteByRowId(data.id, gridApi);
      if (!route || !planMediaDataId) return;

      gridApi?.applyServerSideTransaction({ route, remove: [data] });

      if (data.id === id) {
        navigate({
          search: (search) =>
            chooseLevel(
              { level: MP_LEVELS.MEDIA_PLAN, mediaDataId: planMediaDataId },
              search,
            ),
        });
        enqueueSnackbar({
          title: t('The activity or flight you were viewing has been deleted'),
          subTitle: t("You've been redirected to the media plan"),
          variant: 'warning',
          removable: true,
        });
      }
    },
  );

  const handleMediaLineCreated = useEffectEvent(
    ({
      data,
      parentId,
    }: z.infer<typeof onMediaLineCreated.config.payloadSchema>) => {
      const getRoute = () => {
        if (parentId === planMediaDataId) return [];
        const parentRoute = getRouteByRowId(parentId, gridApi);
        return parentRoute ? [...parentRoute, parentId] : null;
      };

      const route = getRoute();
      if (!route) return;

      gridApi?.applyServerSideTransaction({
        route,
        add: [data],
        addIndex: 0,
      });
    },
  );

  const handleMediaLineUpdated = useEffectEvent(
    ({ data }: z.infer<typeof mediaLineUpdatedPayloadSchema>) => {
      const route = getRouteByRowId(data.id, gridApi);
      const rowNode = gridApi?.getRowNode(data.id);
      if (!route || !rowNode?.id) return;

      if (isIncomingUpdateOutdated(rowNode?.data?.updatedAt, data.updatedAt))
        return;

      gridApi?.applyServerSideTransaction({
        route,
        update: [data],
      });

      if (ff.relatedFieldsFixPlaceholder.enabled) {
        gridApi?.refreshCells({ rowNodes: [rowNode], force: true });
      }

      const context = gridApi?.getGridOption('context');
      if (data.hasChildren && context?.rowIdsToExpand.includes(data.id)) {
        const parentRowNode = gridApi?.getRowNode(data.id);
        if (parentRowNode) gridApi?.setRowNodeExpanded(parentRowNode, true);
        gridApi?.setGridOption('context', {
          ...context,
          rowIdsToExpand: context?.rowIdsToExpand.filter(
            (rowId: string) => rowId !== data.id,
          ),
        });
      }
    },
  );

  const handleTotalsUpdated = useEffectEvent(
    (totalsRow: MediaPlan.MediaData) => {
      queryClient.setQueryData(
        MediaPlan.getTotalsRowQueryOptions({ urlParams: { planId } }).queryKey,
        () => totalsRow,
      );
    },
  );

  const handleMediaPlanSummaryUpdated = useEffectEvent(() => {
    refetchMediaPlanSummary();
  });

  const handleActivitySummaryUpdated = useEffectEvent(() => {
    refetchActivitySummary();
  });

  const handleFlightSummaryUpdated = useEffectEvent(() => {
    refetchFlightSummary();
  });

  const handlePlanningColumnsUpdated = useEffectEvent(
    (
      payload: z.infer<typeof onPlanningColumnsUpdated.config.payloadSchema>,
    ) => {
      const views = payload?.views;
      if (views && !views.includes(columnsViewType)) return;

      refetchColumns();

      if (gridApi) {
        const groupLevelState = gridApi.getServerSideGroupLevelState();

        groupLevelState.forEach((levelState) => {
          gridApi.refreshServerSide({ route: levelState.route });
        });
      }
    },
  );

  useEffect(() => {
    const unsubscribeFns: Unsubscribe[] = [
      onJoinFailed(handleJoinFailed),
      onActivityCreated(handleActivityCreated),
      onActivityDeleted(handleActivityDeleted),
      onActivityUpdated(handleActivityUpdated),
      onFlightCreated(handleFlightCreated),
      onFlightUpdated(handleFlightUpdated),
      onFlightDeleted(handleFlightDeleted),
      onMediaLineDeleted(handleMediaLineDeleted),
      onMediaLineCreated(handleMediaLineCreated),
      onMediaLineUpdated(handleMediaLineUpdated),
      onMediaPlanTotalsUpdated(handleTotalsUpdated),
      onMediaPlanSummaryUpdated(handleMediaPlanSummaryUpdated),
      onActivitySummaryUpdated(handleActivitySummaryUpdated),
      onFlightSummaryUpdated(handleFlightSummaryUpdated),
      onPlanningColumnsUpdated(handlePlanningColumnsUpdated),
    ];

    return () => {
      for (const unsub of unsubscribeFns) {
        unsub();
      }
    };
  }, []);
};
